import React, {FC, SyntheticEvent, useCallback} from 'react';
import './NotificationFlag.less';
import IconButton from '../IconButton/IconButton';
import Icons, { IconType } from '../IconButton/Icons';
import TextField from '../TextField';
import Auth from '../../Auth/Auth';
import i18n from '../../i18n/i18n';
import {formatUserNameFromId} from "../../Contacts/ContactFormat";
import {getCurrentUserId} from "../../lib/picos/currentLogin";
import {isMobile} from "../../lib/picos/browserFns";
import {TaskNotification} from "../../entities/task";

const NOTIFICATION_STATUS_REVIEW = 'r';
const NOTIFICATION_STATUS_COMPLETE = 'c';
const NOTIFICATION_STATUS_WAITING = 'w';

/**
 * This task has been marked for the user to review.
 */
const S_NOTIFICATION_STATUS_REVIEW = i18n.ls('task', 'notificationStatusReview');
const S_NOTIFICATION_STATUS_REVIEW_OWNER = i18n.ls('task', 'notificationStatusReviewOwner');

/**
 * This task has been reviewed, updated and approved.
 */
const S_NOTIFICATION_STATUS_COMPLETE = i18n.ls('task', 'notificationStatusComplete');

/**
 * This task is waiting for the owner's approval.
 */
const S_NOTIFICATION_STATUS_WAITING = i18n.ls('task', 'notificationStatusWaiting');
const S_NOTIFICATION_STATUS_WAITING_OWNER = i18n.ls('task', 'notificationStatusWaitingOwner');

/**
 * Mapping of status code to message.
 */
const NOTIFICATION_STATUS_MAP = [
	{code: NOTIFICATION_STATUS_REVIEW, message: S_NOTIFICATION_STATUS_REVIEW},
	{code: NOTIFICATION_STATUS_WAITING, message: S_NOTIFICATION_STATUS_WAITING},
	{code: NOTIFICATION_STATUS_COMPLETE, message: S_NOTIFICATION_STATUS_COMPLETE},
];

const NOTIFICATION_STATUS_MAP_OWNER = [
	{code: NOTIFICATION_STATUS_REVIEW, message: S_NOTIFICATION_STATUS_REVIEW_OWNER},
	{code: NOTIFICATION_STATUS_WAITING, message: S_NOTIFICATION_STATUS_WAITING_OWNER},
	{code: NOTIFICATION_STATUS_COMPLETE, message: S_NOTIFICATION_STATUS_COMPLETE},
]

/**
 * Get the matching notification message for the code.
 *
 * @param {string} code
 * @param {boolean} [isOwner] - are we the owner (true), or the target (false)
 * @returns {string} - message
 */
const getNotificationMessage = (code: string, isOwner: boolean): string => {
	return isOwner
		   ? NOTIFICATION_STATUS_MAP_OWNER.find(c => c.code === code)?.message ?? '?'
		   : NOTIFICATION_STATUS_MAP.find(c => c.code === code)?.message ?? '?';
}


/**
 * Notifications are used to let the user know that something needs to be addressed.
 * This widget is placed at the top of a page (like TaskItem).
 *
 * There is a different user experience based on if this is the person flagging an item, or if this is a person
 * who has been flagged.
 *
 * States:
 * 1. HIDDEN:  if no notification status and props.target === current user we just hide. Can't flag yourself.
 * 2. AVAILABLE: if no notification status and props.target !== current user we show an empty flag.
 * 3. SET: if notification status is 'r' or 'c' we show a filled flag (to the target), or an outlined flag (to the owner).
 *    a. If status is 'r' we show a message
 *    b. If status is 'c' we do not show a message
 *
 * @class
 */
const NotificationFlag: FC<NotificationFlagProps> = props => {
	/**
	 * Is this task marked for review?
	 * @param {Notification} notification
	 * @returns {boolean}
	 */
	const isMarkedForReview = (notification: TaskNotification): boolean => notification.status === 'r';

	/**
	 * Is this task marked as completed?
	 *
	 * @param {Notification} notification
	 * @returns {boolean}
	 */
	const isCompleted = (notification: TaskNotification): boolean => notification.status === NOTIFICATION_STATUS_COMPLETE;

	/**
	 * Is this task waiting for approval?
	 *
	 * @param {Notification} notification
	 * @returns {boolean}
	 */
	const isWaitingForApproval = (notification: TaskNotification): boolean => notification.status === NOTIFICATION_STATUS_WAITING;

	/**
	 * Is this task not marked for notification?
	 *
	 * @param {Notification} notification
	 * @returns {boolean}
	 */
	const isUnset = (notification: TaskNotification): boolean => !notification.status;

	/**
	 * Cancel this notification
	 */
	const onCancel = () => {
		props.onChange(null);
	}

	/**
	 * Called on flag click (any type).  This is a state machine:
	 *
	 * Current Status ->  Future Status
	 * null               r
	 * r                  (from) null; (to) w
	 * w                  c (or back to r)
	 */
	const onClick = useCallback(() => {
		const notification = {...props.notification};
		const {target} = props;
		const {from, message} = notification;
		const thisUserId = getCurrentUserId();
		const iAmFrom = from === thisUserId;

		if (isUnset(notification) || isCompleted(notification)) {
			// Set the status to [r]eview
			notification.status = NOTIFICATION_STATUS_REVIEW;
			notification.from = thisUserId;
			notification.to = target;
			notification.message = message ?? i18n.ls('task', 'notificationPleaseReview');

			props.onChange(notification);
		}
		else if (isMarkedForReview(notification)) {
			// If this is the person who set the review status, cancel it
			if (iAmFrom) {
				notification.status = null;
				notification.from = null;
				notification.to = null;
				notification.message = null;
			}
			// Otherwise set it as waiting for review
			else notification.status = NOTIFICATION_STATUS_WAITING;

			props.onChange(notification);
		}
		else if (isWaitingForApproval(notification) && iAmFrom) {
			// Mark uncompleted
			notification.status = NOTIFICATION_STATUS_COMPLETE;

			props.onChange(notification);
		}
	}, [props]);

	const onBlurMessage = useCallback((_e: SyntheticEvent, data: TaskNotification, text: string) => {
		const notification = {...data};
		notification.message = text;

		props.onChange(notification);
	}, [props]);

	const onThumbsUp = useCallback(() => {
		const notification = {...props.notification};
		notification.status = NOTIFICATION_STATUS_COMPLETE;

		props.onChange(notification);
	}, [props]);

	const onThumbsDown = useCallback(() => {
		const notification = {...props.notification};
		notification.status = 'r';

		props.onChange(notification);
	}, [props]);


	const {notification, target, isGroup} = props;
	const {status, from, to, message} = (notification || {});

	const thisUserId = getCurrentUserId();
	const iAmFrom = from === thisUserId;
	const iAmTo = to === thisUserId || (isGroup && to && Auth.isInGroup(to));
	const iAmTarget = target === thisUserId;
	const iAmOwner = Auth.isOwner();
	const isHidden = (!status && iAmTarget) || (!props.onChange && !iAmFrom && !iAmTo);
	const showMessage = !!status;
	const isFlaggedForReview = status === NOTIFICATION_STATUS_REVIEW;
	const isWaiting = status === NOTIFICATION_STATUS_WAITING;

	const userName = from ? formatUserNameFromId(from, {shorten: true}) : '';

	// Color schemes:
	// Status     From User             To User
	// (r)        Outline Red           Filled Red
	// (w)        Outline Orange        Filled Orange
	// (c)        Thin Checkered Flag   DuoTone Checkered Flag
	// none       Outline Black         Nothing
	let flagColor: string, flagIcon: IconType, flagTitle: string;

	switch (status) {
		case NOTIFICATION_STATUS_REVIEW:
			flagColor = 'maroon';
			flagIcon = iAmTarget ? Icons.FLAG_FILLED : Icons.FLAG;
			flagTitle = iAmTarget ? 'Your review required. Click flag when done.' : 'Review requested';
			break;
		case NOTIFICATION_STATUS_WAITING:
			flagColor = 'darkorange';
			flagIcon = iAmTarget ? Icons.FLAG_FILLED : Icons.FLAG;
			flagTitle = iAmFrom ? 'Awaiting your approval' : 'Waiting for approval';
			break;
		case NOTIFICATION_STATUS_COMPLETE:
			flagColor = 'black';
			flagIcon = Icons.FLAG_CHECKERED;
			flagTitle = 'Completed and approved';
			break;
		default:
			flagColor = 'black';
			flagIcon = Icons.FLAG;
			flagTitle = 'Mark for review';
	}

	const clsName = !isUnset(notification) ? `notification-flag--${status}` : '';

	if (!isHidden && props.flagOnly) return <div className={`notification-flag ${clsName} flag-only`.trim()}>
		<IconButton type={flagIcon}
					color={flagColor}
					size={props.onChange ? 'large' : 'small'}
					title={flagTitle}
					onClick={props.onChange ? onClick : undefined}
		/>
	</div>;

	// If I am the owner
	else if (!isHidden) return <div className={`notification-flag ${clsName}`.trim()}>
		<div className='notification-flag__title'>{getNotificationMessage(notification.status, iAmFrom)}</div>
		<div className='notification-flag__info'>
			{!(iAmFrom && status === 'r') && <IconButton type={flagIcon}
														 color={flagColor}
														 size={props.onChange ? 'large' : 'small'}
														 title={flagTitle}
			/>}

			{/* Show message as read-only if I am the target */}
			{showMessage && iAmTarget && props.onChange &&
				<>
					<div className='notification-flag__message'>
						<div dangerouslySetInnerHTML={TextField.createMarkup(message)}/>
						<div className='notification-flag__message-user'>{userName}</div>
					</div>
				</>
			}

			{/* Show the message as read-write if I am the assigner */}
			{showMessage && iAmFrom && props.onChange &&
				<TextField className='notification-flag__input-message'
						   onBlur={onBlurMessage}
						   data={props.notification}
						   defaultValue={message}
						   autoFocus={!isMobile({tablet: true, featureDetect: true})}
						   stripHtml={true}
				/>
			}

			{/* Thumbs Up if ready to finish and move up the chain */}
			{iAmTarget && isFlaggedForReview && <IconButton type={Icons.THUMBS_UP}
															color='green'
															title='Click when done'
															onClick={onClick}
															beat={true}
			/>}

			{/* Show approve/reject if status is (w)aiting */}
			{iAmFrom && isWaiting && props.onChange &&
				<div className='notification-flag__thumbs'>
					<IconButton type={Icons.THUMBS_UP} title='Approve' onClick={onThumbsUp} beat={true}/>
					<IconButton type={Icons.THUMBS_DOWN} title='Reject' onClick={onThumbsDown}/>
				</div>
			}

			{/* Always allow delete if owner */}
			{(iAmFrom || iAmOwner) && <IconButton type={Icons.DELETE}
												  onClick={onCancel}
												  color='maroon'
												  title={i18n.l('task', 'notificationCancel')}
			/>}
		</div>
	</div>;
	else return null;
}

export type NotificationStatus = 'r' | 'c' | 'w';

export type NotificationFlagProps = {
	// Only show the flag?
	flagOnly?: boolean;

	// Notification object from Task
	notification: TaskNotification;

	// Is the notification.to a group?
	isGroup?: boolean;

	// Who is the potential target?
	target: string; // Assuming uuid is a string

	// Callback when a notification is changed
	onChange?: (notification: TaskNotification | null) => void;
};

export default NotificationFlag;
